import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, query, where, orderBy, onSnapshot, addDoc, doc, updateDoc } from 'firebase/firestore';
import { SupportTicket, SupportMessage } from '../types';
import { notify } from '../lib/toast';
import { useTranslation } from 'react-i18next';

interface SupportUIProps {
    mode: 'widget' | 'page';
    onClose?: () => void;
}

const SupportUI: React.FC<SupportUIProps> = ({ mode, onClose }) => {
    const { t } = useTranslation('support');
    const { currentUser: user } = useAuth();
    const [view, setView] = useState<'list' | 'chat' | 'new'>('list');
    const [tickets, setTickets] = useState<SupportTicket[]>([]);
    const [activeTicket, setActiveTicket] = useState<SupportTicket | null>(null);
    const [messages, setMessages] = useState<SupportMessage[]>([]);
    const [inputMessage, setInputMessage] = useState('');
    const [newTicketSubject, setNewTicketSubject] = useState('');
    const [newTicketMessage, setNewTicketMessage] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!user) return;

        const q = query(
            collection(db, 'support_tickets'),
            where('userId', '==', user.id),
            orderBy('updatedAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const ticketData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SupportTicket));
            setTickets(ticketData);

            if (activeTicket) {
                const updated = ticketData.find(t => t.id === activeTicket.id);
                if (updated) setActiveTicket(updated);
            }
        });

        return () => unsubscribe();
    }, [user, activeTicket?.id]);

    useEffect(() => {
        if (!activeTicket) return;

        const q = query(
            collection(db, `support_tickets/${activeTicket.id}/messages`),
            orderBy('createdAt', 'asc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setMessages(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SupportMessage)));

            if (activeTicket.unreadUser) {
                updateDoc(doc(db, 'support_tickets', activeTicket.id), { unreadUser: false });
            }
        });

        return () => unsubscribe();
    }, [activeTicket]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleCreateTicket = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || !newTicketSubject.trim() || !newTicketMessage.trim()) return;

        try {
            const now = Date.now();
            const ticketRef = await addDoc(collection(db, 'support_tickets'), {
                userId: user.id,
                userName: user.name || t('ui.user'),
                userEmail: user.email,
                subject: newTicketSubject,
                status: 'OPEN',
                priority: 'MEDIUM',
                createdAt: now,
                updatedAt: now,
                lastMessage: newTicketMessage,
                lastMessageAt: now,
                unreadUser: false,
                unreadAgent: true
            });

            await addDoc(collection(db, `support_tickets/${ticketRef.id}/messages`), {
                senderId: user.id,
                senderRole: 'user',
                text: newTicketMessage,
                createdAt: now,
            });

            notify.success(t('ui.ticketCreated'));
            setNewTicketSubject('');
            setNewTicketMessage('');
            setView('list');
        } catch (error) {
            console.error("Error creating ticket:", error);
            notify.error(t('ui.ticketCreateError'));
        }
    };

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || !activeTicket || !inputMessage.trim()) return;

        try {
            const now = Date.now();
            await addDoc(collection(db, `support_tickets/${activeTicket.id}/messages`), {
                senderId: user.id,
                senderRole: 'user',
                text: inputMessage.trim(),
                createdAt: now
            });

            await updateDoc(doc(db, 'support_tickets', activeTicket.id), {
                lastMessage: inputMessage.trim(),
                lastMessageAt: now,
                updatedAt: now,
                unreadAgent: true,
                status: activeTicket.status === 'RESOLVED' ? 'OPEN' : activeTicket.status
            });

            setInputMessage('');
        } catch (error) {
            console.error("Error sending message:", error);
            notify.error(t('ui.messageSendError'));
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'OPEN': return 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20';
            case 'IN_PROGRESS': return 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20';
            case 'RESOLVED': return 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700';
            case 'CLOSED': return 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700';
            default: return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700';
        }
    };

    if (!user) return null;

    const isPage = mode === 'page';

    return (
        <div className={`flex flex-col bg-gray-50 dark:bg-gray-950 ${isPage ? 'h-[700px] rounded-[2rem] shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden' : 'h-full overflow-hidden'}`}>
            {/* Header */}
            <div className={`bg-gray-950 dark:bg-gray-900 text-white p-6 flex justify-between items-center shrink-0 border-b border-white/5 ${isPage ? 'rounded-t-[2rem]' : ''}`}>
                <div>
                    <h3 className="font-black text-xl tracking-tighter uppercase italic text-white leading-none">Fast<span className="text-amber-500">Cash</span><span className="text-blue-500 tracking-normal not-italic uppercase ml-0.5">TRADE</span></h3>
                    <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white/30 mt-1">{t('widget.headerTitle')}</p>
                    <p className="text-[8px] font-black uppercase tracking-[0.2em] text-blue-400 mt-0.5">{t('widget.verifiedChannel')}</p>
                </div>
                <div className="flex gap-2">
                    {view !== 'list' && (
                        <button onClick={() => setView('list')} className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all text-[10px] font-black uppercase tracking-widest active:scale-95">
                            {t('ui.back')}
                        </button>
                    )}
                    {!isPage && onClose && (
                        <button onClick={onClose} className="p-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all group active:scale-95">
                            <svg className="w-5 h-5 group-hover:rotate-90 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    )}
                </div>
            </div>

            {/* Content Container */}
            <div className={`flex-1 flex overflow-hidden ${isPage ? 'flex-row' : 'flex-col'}`}>

                {/* Desktop Side List (Only in Page mode) */}
                {isPage && (
                    <div className="w-80 border-r border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 overflow-y-auto p-4 hidden md:block">
                        <div className="flex justify-between items-center mb-6 px-2">
                            <h4 className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">{t('ui.yourTickets')}</h4>
                            <button onClick={() => setView('new')} className="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest hover:underline">{t('ui.new')}</button>
                        </div>
                        <div className="space-y-3">
                            {tickets.map(ticket => (
                                <button
                                    key={ticket.id}
                                    onClick={() => { setActiveTicket(ticket); setView('chat'); }}
                                    className={`w-full p-5 rounded-2xl text-left transition-all flex justify-between items-start group relative border ${activeTicket?.id === ticket.id ? 'bg-gray-950 dark:bg-white border-transparent shadow-xl' : 'bg-gray-50 dark:bg-gray-900 border-gray-100 dark:border-gray-800 hover:border-blue-500/30'}`}
                                >
                                    <div className="flex-1 min-w-0 pr-2">
                                        <h5 className={`font-black text-sm truncate uppercase tracking-tight ${activeTicket?.id === ticket.id ? 'text-white dark:text-gray-950' : 'text-gray-900 dark:text-white'}`}>{ticket.subject}</h5>
                                        <p className={`text-xs truncate mt-1.5 font-medium ${activeTicket?.id === ticket.id ? 'text-white/70 dark:text-gray-950/70' : 'text-gray-500 dark:text-gray-400'}`}>{ticket.lastMessage}</p>
                                    </div>
                                    {ticket.unreadUser && <span className="w-2.5 h-2.5 bg-rose-500 rounded-full shrink-0 shadow-lg shadow-rose-500/30 animate-pulse mt-1"></span>}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Main Content Area */}
                <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-gray-950">
                    {/* List View (Widget mode OR Mobile Page mode) */}
                    {(view === 'list' && !activeTicket) && (
                        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-950">
                            <div className="flex justify-between items-center mb-2 px-2">
                                <h4 className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">{t('ui.yourTickets')}</h4>
                                <button onClick={() => setView('new')} className="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest hover:underline">{t('ui.newTicket')}</button>
                            </div>

                            {tickets.length === 0 ? (
                                <div className="text-center py-20 px-6">
                                    <div className="text-7xl mb-6 grayscale dark:invert opacity-30">🎧</div>
                                    <p className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tighter">{t('ui.needHelp')}</p>
                                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mt-2">{t('ui.createTicketDesc')}</p>
                                    <button
                                        onClick={() => setView('new')}
                                        className="mt-8 px-10 py-4 bg-gray-950 dark:bg-white text-white dark:text-gray-950 rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-2xl active:scale-95 transition-all"
                                    >
                                        {t('ui.createTicketBtn')}
                                    </button>
                                </div>
                            ) : (
                                tickets.map(ticket => (
                                    <button
                                        key={ticket.id}
                                        onClick={() => { setActiveTicket(ticket); setView('chat'); }}
                                        className="w-full bg-white dark:bg-gray-900 p-5 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 text-left hover:scale-[1.01] transition-all active:scale-95 flex justify-between items-start group"
                                    >
                                        <div className="flex-1 min-w-0 pr-4">
                                            <div className="flex justify-between items-start gap-2 mb-2">
                                                <span className={`text-[9px] font-black uppercase px-2.5 py-1 rounded-lg tracking-widest ${getStatusColor(ticket.status)}`}>
                                                    {ticket.status}
                                                </span>
                                                {ticket.unreadUser && <span className="w-2.5 h-2.5 bg-rose-500 rounded-full shadow-lg shadow-rose-500/30 animate-pulse"></span>}
                                            </div>
                                            <h5 className="font-black text-gray-900 dark:text-white text-sm truncate tracking-tight uppercase">{ticket.subject}</h5>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1.5 font-medium group-hover:text-gray-900 dark:group-hover:text-white">{ticket.lastMessage}</p>
                                        </div>
                                        <span className="text-[10px] font-black text-gray-300 dark:text-gray-600 whitespace-nowrap pt-1">
                                            {new Date(ticket.lastMessageAt).toLocaleDateString()}
                                        </span>
                                    </button>
                                ))
                            )}
                        </div>
                    )}

                    {/* NEW TICKET VIEW */}
                    {view === 'new' && (
                        <div className="flex-1 flex flex-col bg-gray-50 dark:bg-gray-950">
                            <form onSubmit={handleCreateTicket} className={`p-8 space-y-6 overflow-y-auto max-w-2xl mx-auto w-full`}>
                                <div className="text-center mb-10">
                                    <h2 className="text-3xl font-black text-gray-900 dark:text-white uppercase tracking-tighter italic">{t('ui.newTicketTitle')}</h2>
                                    <p className="text-gray-500 dark:text-gray-400 font-medium">{t('ui.newTicketSubtitle')}</p>
                                </div>
                                <div className="bg-white dark:bg-gray-900 p-8 rounded-[2.5rem] shadow-sm border border-gray-100 dark:border-gray-800 space-y-6">
                                    <div>
                                        <label className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest ml-1 mb-2 block">{t('ui.categoryLabel')}</label>
                                        <input
                                            className="w-full p-4 rounded-2xl bg-gray-50 dark:bg-gray-950 border border-gray-100 dark:border-gray-800 focus:border-blue-500/30 focus:bg-white dark:focus:bg-gray-950 focus:ring-4 focus:ring-blue-500/5 text-sm font-bold outline-none transition-all text-gray-900 dark:text-white"
                                            placeholder={t('ui.categoryPlaceholder')}
                                            value={newTicketSubject}
                                            onChange={e => setNewTicketSubject(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest ml-1 mb-2 block">{t('ui.messageLabel')}</label>
                                        <textarea
                                            className="w-full p-4 rounded-2xl bg-gray-50 dark:bg-gray-950 border border-gray-100 dark:border-gray-800 focus:border-blue-500/30 focus:bg-white dark:focus:bg-gray-950 focus:ring-4 focus:ring-blue-500/5 text-sm font-bold outline-none transition-all min-h-[180px] text-gray-900 dark:text-white resize-none"
                                            placeholder={t('ui.messagePlaceholder')}
                                            value={newTicketMessage}
                                            onChange={e => setNewTicketMessage(e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <button
                                        type="button"
                                        onClick={() => setView('list')}
                                        className="flex-1 py-5 bg-gray-100 dark:bg-gray-900 text-gray-500 dark:text-gray-400 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-gray-200 dark:hover:bg-gray-800 transition-all"
                                    >
                                        {t('ui.cancel')}
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-[2] py-5 bg-gray-950 dark:bg-white text-white dark:text-gray-950 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-2xl active:scale-95 transition-all"
                                    >
                                        {t('ui.submit')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* CHAT VIEW (Main) */}
                    {(view === 'chat' || (isPage && activeTicket)) && activeTicket ? (
                        <div className="flex-1 flex flex-col h-full bg-white dark:bg-gray-950 relative">
                            {/* Ticket Info Header (Page Only) */}
                            {isPage && (
                                <div className="p-5 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-white dark:bg-gray-950 z-10 shadow-sm">
                                    <div className="flex items-center gap-4">
                                        <div className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest ${getStatusColor(activeTicket.status)}`}>
                                            {activeTicket.status}
                                        </div>
                                        <h4 className="font-black text-gray-900 dark:text-white uppercase tracking-tight">{activeTicket.subject}</h4>
                                    </div>
                                    <span className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest border border-gray-100 dark:border-gray-800 px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-900">
                                        {t('ui.refPrefix')} #{activeTicket.id.slice(0, 8).toUpperCase()}
                                    </span>
                                </div>
                            )}

                            {/* Messages Container */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50/10 dark:bg-gray-950/20">
                                {messages.map(msg => {
                                    const isMe = msg.senderRole === 'user';
                                    const isSystem = msg.senderRole === 'admin' || msg.senderRole === 'support' || msg.senderRole === 'owner';

                                    if (msg.isInternalNote) return null;

                                    return (
                                        <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                                            <div className={`max-w-[85%] md:max-w-[70%] p-6 rounded-[2.5rem] shadow-sm relative group transition-all ${isMe
                                                ? 'bg-gray-950 dark:bg-white text-white dark:text-gray-950 rounded-br-lg border border-white/10 dark:border-gray-200'
                                                : 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800 rounded-bl-lg'
                                                }`}>
                                                {isSystem && (
                                                    <div className="flex items-center gap-2 mb-3">
                                                        <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
                                                        <div className="text-[10px] font-black uppercase text-blue-600 dark:text-blue-400 tracking-widest">{t('ui.supportUnit')}</div>
                                                    </div>
                                                )}
                                                <p className="font-medium leading-[1.6] text-[15px]">{msg.text}</p>
                                                <div className={`text-[9px] font-black uppercase tracking-widest mt-3 flex items-center justify-end gap-1.5 opacity-60 ${isMe ? 'text-white/80 dark:text-gray-950/80' : 'text-gray-400'}`}>
                                                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                                    {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Message Input */}
                            <form onSubmit={handleSendMessage} className={`p-6 bg-white dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800 shrink-0 ${isPage ? 'pb-10' : ''}`}>
                                <div className="max-w-5xl mx-auto flex gap-4">
                                    <div className="flex-1 relative group">
                                        <input
                                            className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-800 focus:border-blue-500/30 focus:bg-white dark:focus:bg-gray-950 focus:ring-4 focus:ring-blue-500/5 rounded-2xl px-6 py-4 text-sm font-bold outline-none transition-all text-gray-900 dark:text-white"
                                            placeholder={activeTicket.status === 'CLOSED' ? t('ui.ticketClosed') : t('ui.writeMessage')}
                                            value={inputMessage}
                                            onChange={e => setInputMessage(e.target.value)}
                                            disabled={activeTicket.status === 'CLOSED'}
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={!inputMessage.trim() || activeTicket.status === 'CLOSED'}
                                        className="w-14 h-14 bg-gray-950 dark:bg-white text-white dark:text-gray-950 rounded-2xl shadow-2xl flex items-center justify-center disabled:opacity-20 disabled:grayscale hover:scale-105 active:scale-95 transition-all group"
                                    >
                                        <svg className="w-6 h-6 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                                    </button>
                                </div>
                            </form>
                        </div>
                    ) : (isPage && view === 'list' && tickets.length > 0) && (
                        <div className="hidden md:flex flex-1 items-center justify-center bg-gray-50/20 dark:bg-gray-950/40 text-center p-8">
                            <div className="max-w-xs">
                                <div className="text-8xl mb-8 grayscale dark:invert opacity-10">💬</div>
                                <h3 className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tighter">{t('ui.panelTitle')}</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mt-3">{t('ui.panelDesc')}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SupportUI;
